import { zodResolver } from '@hookform/resolvers/zod'
import { Building2, Loader2, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ConfirmModal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/utils'
import { parseEmployeeReassignmentWarning, useDepartments, useManagerOptions, useSaveDepartment } from '../api'
import type { AccessUser, Department, DepartmentInput, EmployeeReassignmentWarning } from '../types'
import { toastError } from '../ui'
import { FormDrawer } from './FormDrawer'

type Props = { open: boolean; onOpenChange: (v: boolean) => void; department: Department | null }

type FormValues = {
  name: string
  description: string
  managerId: string
  parentId: string
  isActive: boolean
}

function descendantsOf(id: string, departments: Department[]) {
  const blocked = new Set([id])
  let grew = true
  while (grew) {
    grew = false
    for (const d of departments) {
      if (d.parentId && blocked.has(d.parentId) && !blocked.has(d.id)) {
        blocked.add(d.id)
        grew = true
      }
    }
  }
  return blocked
}

function sortedIds(ids: string[]) {
  return [...new Set(ids.filter(Boolean))].sort()
}

function sameIds(a: string[], b: string[]) {
  const left = sortedIds(a)
  const right = sortedIds(b)
  return left.length === right.length && left.every((id, i) => id === right[i])
}

function personAvatar(u: Pick<AccessUser, 'name' | 'avatarUrl'>, className = 'size-7') {
  return <Avatar name={u.name} src={u.avatarUrl} className={className} />
}

export function DepartmentFormDialog({ open, onOpenChange, department }: Props) {
  return open ? <DepartmentForm key={department?.id ?? 'new'} department={department} onDone={() => onOpenChange(false)} /> : null
}

function DepartmentForm({ department, onDone }: { department: Department | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveDepartment()
  const departments = useDepartments().data ?? []
  const managers = useManagerOptions(true).data ?? []
  const [memberQuery, setMemberQuery] = useState('')
  const [memberIds, setMemberIds] = useState<string[]>(() => department?.members.map((m) => m.id) ?? [])
  const [current, setCurrent] = useState<Department | null>(department)
  const [pendingConfirm, setPendingConfirm] = useState<{
    values: FormValues
    memberIds: string[]
    includeMembers: boolean
    warning: EmployeeReassignmentWarning
  } | null>(null)

  const people = useMemo(() => {
    const byId = new Map<string, AccessUser>()
    for (const u of managers) byId.set(u.id, u)
    const seed = current ?? department
    console.log("ne geldi", seed)
    if (seed?.manager && !byId.has(seed.manager.id)) {
      byId.set(seed.manager.id, {
        id: seed.manager.id,
        name: seed.manager.name,
        email: seed.manager.email,
        departmentId: seed.id,
        department: seed.name,
        position: '',
        avatarUrl: seed.manager.avatarUrl,
      })
    }
    for (const m of seed?.members ?? []) {
      if (byId.has(m.id)) continue
      byId.set(m.id, {
        id: m.id,
        name: m.name,
        email: m.email,
        departmentId: seed?.id ?? '',
        department: seed?.name ?? '',
        position: m.position ?? '',
        avatarUrl: m.avatarUrl,
      })
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'tr'))
  }, [current, department, managers])

  const schema = z.object({
    name: z.string().trim().min(2, t('access.departments.form.nameRequired')).max(80),
    description: z.string().trim().max(500),
    managerId: z.string().min(1, t('access.departments.form.managerRequired')),
    parentId: z.string(),
    isActive: z.boolean(),
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: department?.name ?? '',
      description: department?.description ?? '',
      managerId: department?.managerId ?? '',
      parentId: department?.parentId ?? '',
      isActive: department?.isActive ?? true,
    },
  })
  const { errors } = form.formState
  const managerId = useWatch({ control: form.control, name: 'managerId' })
  const description = useWatch({ control: form.control, name: 'description' }) ?? ''
  const isActive = useWatch({ control: form.control, name: 'isActive' }) ?? true
  const blockedParents = department ? descendantsOf(department.id, departments) : new Set<string>()

  const filteredPeople = useMemo(() => {
    const q = memberQuery.trim().toLocaleLowerCase('tr')
    if (!q) return people
    return people.filter((u) => [u.name, u.email, u.position].some((v) => v.toLocaleLowerCase('tr').includes(q)))
  }, [memberQuery, people])

  const toggleMember = (id: string) => {
    setMemberIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const applySaved = (saved: Department) => {
    setCurrent(saved)
    setMemberIds(saved.members.map((m) => m.id))
    form.reset({
      name: saved.name,
      description: saved.description,
      managerId: saved.managerId ?? '',
      parentId: saved.parentId ?? '',
      isActive: saved.isActive,
    })
  }

  const editingId = current?.id ?? department?.id

  const submit = async (values: FormValues, opts: { confirm: boolean; memberIds: string[]; includeMembers: boolean }) => {
    const input: DepartmentInput = {
      name: values.name,
      description: values.description,
      managerId: values.managerId,
      parentId: values.parentId || null,
      isActive: values.isActive,
      confirmEmployeeReassignment: opts.confirm,
      ...(opts.includeMembers ? { memberIds: opts.memberIds } : {}),
    }
    try {
      const saved = await save.mutateAsync({ id: editingId, input })
      applySaved(saved)
      toast.success(t(editingId ? 'access.departments.updated' : 'access.departments.created'))
      setPendingConfirm(null)
      onDone()
    } catch (e) {
      if (!opts.confirm && isApiError(e) && e.status === 409) {
        const warning = parseEmployeeReassignmentWarning(e.warning)
        if (warning && (e.code === 'EMPLOYEE_REASSIGNMENT_REQUIRES_CONFIRMATION' || warning.operation === 'EMPLOYEE_REASSIGNMENT')) {
          setPendingConfirm({ values, memberIds: opts.memberIds, includeMembers: opts.includeMembers, warning })
          return
        }
      }
      toastError(e)
    }
  }

  const onSubmit = form.handleSubmit((values) => {
    const nextIds = sortedIds(memberIds)
    const originalIds = sortedIds((current ?? department)?.members.map((m) => m.id) ?? [])
    const includeMembers = !editingId || !sameIds(nextIds, originalIds)
    void submit(values, { confirm: false, memberIds, includeMembers })
  })

  const managerOptions = people.map((u) => ({
    value: u.id,
    label: u.name,
    description: u.position || u.email,
    icon: personAvatar(u),
    keywords: [u.name, u.email, u.position],
  }))
  const parentOptions = [
    { value: '', label: t('access.departments.form.parentNone') },
    ...departments.filter((d) => !blockedParents.has(d.id)).map((d) => ({ value: d.id, label: d.name, description: d.code })),
  ]

  return (
    <>
      <FormDrawer
        open
        onOpenChange={(v) => !v && onDone()}
        dismissible={!save.isPending}
        size="md"
        icon={<Building2 />}
        title={t(editingId ? 'access.departments.form.editTitle' : 'access.departments.form.createTitle')}
        description={t('access.departments.form.hint')}
        onSubmit={(e) => void onSubmit(e)}
        footer={
          <>
            <Button type="button" variant="cancel" onClick={onDone}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : null}
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dep-name">{t('access.departments.form.name')}</Label>
            <Input id="dep-name" aria-invalid={!!errors.name} {...form.register('name')} />
            {errors.name ? <p className="text-xs text-destructive">{errors.name.message}</p> : null}
            {(current ?? department)?.code ? (
              <p className="text-xs text-muted-foreground">
                {t('access.departments.form.code')}: <span className="font-mono">{(current ?? department)?.code}</span>
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="dep-parent">{t('access.departments.form.parent')}</Label>
            <FormSelect id="dep-parent" control={form.control} name="parentId" options={parentOptions} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dep-manager">{t('access.departments.form.manager')}</Label>
            <FormSelect
              id="dep-manager"
              control={form.control}
              name="managerId"
              options={managerOptions}
              searchable
              placeholder={t('access.departments.form.managerPick')}
            />
            {errors.managerId ? <p className="text-xs text-destructive">{errors.managerId.message}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="dep-desc">{t('access.departments.form.description')}</Label>
            <Textarea id="dep-desc" rows={3} maxLength={500} {...form.register('description')} />
            <p className="text-right text-[11px] text-muted-foreground">{description.length}/500</p>
          </div>

          <div className="flex items-center gap-3 rounded-xl border p-4">
            <button
              type="button"
              role="switch"
              aria-checked={isActive}
              aria-label={t(isActive ? 'access.status.active' : 'access.status.passive')}
              onClick={() => form.setValue('isActive', !isActive, { shouldDirty: true })}
              className={cn(
                'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                isActive ? 'bg-primary' : 'bg-muted-foreground/30',
              )}
            >
              <span
                aria-hidden
                className={cn('size-5 rounded-full bg-card shadow-sm transition-transform', isActive ? 'translate-x-[22px]' : 'translate-x-0.5')}
              />
            </button>
            <div className="min-w-0">
              <p className="text-sm font-medium">{t(isActive ? 'access.status.active' : 'access.status.passive')}</p>
              <p className="text-xs text-muted-foreground">{t('access.departments.form.statusHint')}</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>{t('access.departments.form.members')}</Label>
              <span className="text-xs text-muted-foreground">{t('access.departments.form.membersSelected', { count: memberIds.length })}</span>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={memberQuery}
                onChange={(e) => setMemberQuery(e.target.value)}
                placeholder={t('access.departments.detail.memberSearch')}
                className="pl-9"
                aria-label={t('access.departments.detail.memberSearch')}
              />
            </div>
            <ul className="max-h-56 overflow-y-auto rounded-xl border">
              {filteredPeople.length === 0 ? (
                <li className="px-3 py-6 text-center text-sm text-muted-foreground">{t('access.departments.form.membersEmpty')}</li>
              ) : (
                filteredPeople.map((u) => {
                  const isManager = u.id === managerId
                  const checked = memberIds.includes(u.id)
                  return (
                    <li key={u.id} className={cn('border-b last:border-b-0', isManager && 'bg-muted/30')}>
                      <label className="flex cursor-pointer items-center gap-3 px-3 py-2.5">
                        <input
                          type="checkbox"
                          className="size-4 shrink-0 accent-[var(--color-primary)]"
                          checked={checked}
                          onChange={() => toggleMember(u.id)}
                        />
                        {personAvatar(u, 'size-8')}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{u.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">{u.position || u.email || '—'}</span>
                        </span>
                        <Badge tone={isManager ? 'primary' : 'neutral'}>
                          {isManager ? t('access.departments.detail.managerBadge') : t('access.departments.detail.memberBadge')}
                        </Badge>
                      </label>
                    </li>
                  )
                })
              )}
            </ul>
          </div>
        </div>
      </FormDrawer>

      <ConfirmModal
        open={!!pendingConfirm}
        onOpenChange={(v) => !v && !save.isPending && setPendingConfirm(null)}
        title={t('access.departments.reassign.title')}
        description={pendingConfirm?.warning.message || t('access.departments.reassign.body')}
        confirmLabel={t('access.departments.reassign.confirm')}
        loading={save.isPending}
        onConfirm={() => {
          if (!pendingConfirm) return
          void submit(pendingConfirm.values, {
            confirm: true,
            memberIds: pendingConfirm.memberIds,
            includeMembers: pendingConfirm.includeMembers,
          })
        }}
      >
        <ul className="max-h-64 space-y-2 overflow-y-auto">
          {pendingConfirm?.warning.affectedEmployees.map((emp) => (
            <li key={emp.id} className="rounded-lg border px-3 py-2 text-sm">
              <p className="font-medium">{emp.name}</p>
              {emp.email ? <p className="text-xs text-muted-foreground">{emp.email}</p> : null}
              <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                {emp.reasons.includes('OTHER_DEPARTMENT') && emp.currentDepartmentName ? (
                  <li>{t('access.departments.reassign.otherDepartment', { name: emp.currentDepartmentName })}</li>
                ) : null}
                {emp.reasons.includes('POSITION_OTHER_DEPARTMENT') || emp.willClearPosition ? (
                  <li>
                    {emp.willClearPosition
                      ? t('access.departments.reassign.willClearPosition', {
                        title: emp.currentPositionTitle || t('access.departments.reassign.positionFallback'),
                      })
                      : t('access.departments.reassign.positionOther', {
                        title: emp.currentPositionTitle || t('access.departments.reassign.positionFallback'),
                      })}
                  </li>
                ) : null}
              </ul>
            </li>
          ))}
        </ul>
      </ConfirmModal>
    </>
  )
}
